/**
 * Bundle 170 hotfix - Backend crashes tile for /admin/incidents.
 *
 * Live from `GET /api/admin/incidents/crash-log`. Primary metric is
 * `count_24h` (crashes in the trailing 24h). Any non-zero count
 * tints the tile red because a backend crash is never "green".
 *
 * Clicking opens a modal listing the most recent 20 crashes with
 * exc_type, path, timestamp, RSS at crash, and first-line of
 * traceback. A "Clear log" button posts to
 * `/admin/incidents/crash-log/clear` after triage.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RefreshCcw, Trash2 } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

const CrashesTile = () => {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/admin/incidents/crash-log');
      setPayload(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load crash log.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const clearAll = async () => {
    if (clearing) return;
    setClearing(true);
    try {
      await api.post('/admin/incidents/crash-log/clear');
      toast.success('Crash log cleared.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to clear.');
    } finally {
      setClearing(false);
    }
  };

  const count = payload?.count_24h ?? 0;
  const tone = count > 0 ? 'red' : 'green';
  const border = count > 0
    ? 'border-red-300 hover:border-red-400'
    : 'border-neutral-200 hover:border-primary-300';
  const numberCls = count > 0 ? 'text-red-700' : 'text-neutral-500';
  const recent = payload?.recent || [];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="block w-full text-left group"
        data-testid="crashes-tile-button"
        aria-label="Open backend crashes detail"
      >
        <Card
          className={
            'border shadow-soft transition-all hover:shadow-md ' + border
          }
          data-testid="crashes-tile"
          data-tone={tone}
        >
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="h-4 w-4 text-primary-800" />
                  <div className="text-[10px] uppercase tracking-widest text-secondary-700">
                    Backend crashes (24h)
                  </div>
                </div>
                <div className="text-sm text-primary-900 font-semibold mb-2">
                  {loading
                    ? 'Loading.'
                    : error
                      ? 'Could not load'
                      : count > 0
                        ? 'Recent unhandled exceptions'
                        : 'No crashes in the last 24h'}
                </div>
                <div className="flex items-baseline gap-3">
                  <span
                    className={`text-2xl font-semibold ${numberCls}`}
                    data-testid="crashes-tile-count"
                  >
                    {count}
                  </span>
                  <span
                    className="text-[10px] uppercase tracking-widest text-neutral-500"
                  >
                    Retained {payload?.ttl_hours || 24}h
                  </span>
                </div>
                {error ? (
                  <div className="text-xs text-red-700 mt-2">{error}</div>
                ) : null}
              </div>
              <div className="text-xs text-secondary-700 group-hover:text-primary-900 whitespace-nowrap">
                Open detail →
              </div>
            </div>
          </CardContent>
        </Card>
      </button>
      <Dialog open={open} onOpenChange={(v) => { if (!v) setOpen(false); }}>
        <DialogContent
          className="max-w-3xl max-h-[85vh] overflow-y-auto"
          data-testid="crashes-modal"
        >
          <DialogHeader>
            <DialogTitle>Backend crashes (last 24h)</DialogTitle>
            <DialogDescription>
              Bundle 170 crash log. Bounded to 100 rows / 24h.
              Each row captures the exception type, path, timestamp,
              RSS memory at crash, and the last line of the traceback.
              Click a row to view the full stack.
            </DialogDescription>
          </DialogHeader>
          {recent.length === 0 ? (
            <div
              className="text-sm text-neutral-500 border border-neutral-200 rounded-md p-4"
              data-testid="crashes-modal-empty"
            >
              No crashes recorded in the last 24h.
            </div>
          ) : (
            <table
              className="w-full text-xs"
              data-testid="crashes-modal-table"
            >
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-neutral-500">
                  <th className="text-left py-1 pr-3">When</th>
                  <th className="text-left py-1 pr-3">Kind</th>
                  <th className="text-left py-1 pr-3">Path</th>
                  <th className="text-left py-1 pr-3">Exception</th>
                  <th className="text-right py-1">RSS (MB)</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((r) => (
                  <tr
                    key={r.id}
                    className="border-t border-neutral-100"
                    data-testid={`crashes-modal-row-${r.id}`}
                  >
                    <td className="py-1 pr-3 font-mono text-[11px] text-neutral-700">
                      {r.created_at || '-'}
                    </td>
                    <td className="py-1 pr-3 font-mono text-[11px] text-neutral-700">
                      {r.kind || '-'}
                    </td>
                    <td className="py-1 pr-3 font-mono text-[11px] text-primary-900 break-all">
                      {r.method ? `${r.method} ${r.path || '-'}` : (r.path || '-')}
                    </td>
                    <td className="py-1 pr-3 font-mono text-[11px] text-red-700">
                      {r.exc_type || '-'}
                    </td>
                    <td className="py-1 text-right font-mono text-[11px] text-neutral-700">
                      {r.process_rss_mb ?? '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <DialogFooter>
            <Button
              variant="outline" size="sm" onClick={load} disabled={loading}
              data-testid="crashes-modal-refresh"
            >
              <RefreshCcw
                className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`}
              />
              Refresh
            </Button>
            <Button
              variant="outline" size="sm" onClick={clearAll}
              disabled={clearing || recent.length === 0}
              className="text-red-700 border-red-200 hover:bg-red-50"
              data-testid="crashes-modal-clear"
            >
              <Trash2 className="h-4 w-4 mr-1.5" />
              Clear log
            </Button>
            <Button
              variant="default" size="sm" onClick={() => setOpen(false)}
              data-testid="crashes-modal-close"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CrashesTile;
